import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    Grid,
    GridColumn as Column,
    GridToolbar,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { LoadingPanel } from "../LoadingPanel";
import { addNotificationWithTimeout } from '../../app/addNotificationWithTimeout';
import { handleResponse } from '../../app/handleResponse';
import { CommandCell } from '../forms/CommandCell';
import { getCSRFToken } from '../../app/getCSRFToken';
import { Dialog } from "@progress/kendo-react-dialogs";
import { TextArea } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input, Checkbox } from "@progress/kendo-react-inputs";

export const AdministratorProxyServers = () => {
    const dispatch = useDispatch();

    const defaultTicketingWebsite = {
        ticketingWebsiteId: "",
        name: "Select Ticketing Website",
    };

    const [proxyServers, setProxyServers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [newProxyServersText, setNewProxyServersText] = useState("");
    const [ticketingWebsites, setTicketingWebsites] = useState([]);
    const [ticketingWebsite, setTicketingWebsite] = useState(defaultTicketingWebsite);
    const [url, setUrl] = useState(undefined);
    const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false);
    const [lastSelectedProxyServerKey, setLastSelectedProxyServerKey] = useState(undefined);

    const SelectedCell = (props) => {
        const proxyServerKey = props.dataItem.proxyServerKey;
        const selected = props.dataItem.selected;

        return (
            <td className="text-center">
                <Checkbox onChange={(event) => selectedValueChanged(event, proxyServerKey, !selected)} value={selected} />
            </td>
        );
    };

    const SelectedHeaderCell = () => {
        return (
            <div className="text-center">
                <Checkbox onChange={headerCheckboxValueChanged} value={isHeaderCheckboxChecked} />
            </div>
        );
    };

    const selectedValueChanged = (event, proxyServerKey, selected) => {
        setIsLoading(true);

        setTimeout(() => {
            let proxyServersCopy = proxyServers;

            const itemIndex = proxyServersCopy.findIndex(dataItem =>
                dataItem.proxyServerKey === proxyServerKey
            );
            
            let isSelectingMultipleItems = false;

            //Shift selection of multiple items            
            if (event.nativeEvent.shiftKey
                && lastSelectedProxyServerKey !== undefined
                && lastSelectedProxyServerKey !== proxyServerKey) {
                const lastSelectedItemIndex = proxyServersCopy.findIndex(dataItem =>
                    dataItem.proxyServerKey === lastSelectedProxyServerKey
                );

                if (lastSelectedItemIndex >= 0) {
                    isSelectingMultipleItems = true;

                    let lowIndex = -1;
                    let highIndex = -1;

                    if (itemIndex > lastSelectedItemIndex) {
                        lowIndex = lastSelectedItemIndex;
                        highIndex = itemIndex;
                    }
                    else {
                        lowIndex = itemIndex;
                        highIndex = lastSelectedItemIndex;
                    }

                    for (let i = lowIndex; i <= highIndex; i++) {
                        proxyServersCopy[i].selected = selected;
                    }
                }
            }

            if (isSelectingMultipleItems === false) {
                proxyServersCopy[itemIndex].selected = selected;
            }

            if (selected === true) {
                setLastSelectedProxyServerKey(proxyServerKey);
            }

            setProxyServers(proxyServersCopy);

            setIsHeaderCheckboxChecked(false);
            setIsLoading(false);
        }, 1);
    };

    const headerCheckboxValueChanged = () => {
        setIsLoading(true);

        setTimeout(() => {
            const checked = !isHeaderCheckboxChecked;
            setIsHeaderCheckboxChecked(checked);

            let proxyServersCopy = proxyServers;

            proxyServersCopy.forEach(dataItem =>
                dataItem.selected = checked
            );

            setProxyServers(proxyServersCopy);

            setLastSelectedProxyServerKey(undefined);

            setIsLoading(false);
        }, 1);
    };

    const getTicketingWebsites = () => {
        const dataItem = {
            onlyActive: true,
        }

        fetch(`/api/general/ticketingwebsites`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                const allTicketingWebsites = [defaultTicketingWebsite].concat(result);
                setTicketingWebsites(allTicketingWebsites);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const getProxyServers = () => {
        setIsLoading(true);

        fetch(`/api/administrator/proxyservers`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                setIsLoading(false);
                setProxyServers(result.proxyServers);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    }

    const addProxyServers = (newProxyServers) => {
        const dataItem = {
            proxies: newProxyServers,
        };

        fetch("/api/administrator/proxyservers",
            {
                method: "PATCH",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setShowDialog(false);
                setNewProxyServersText("");

                addNotificationWithTimeout(dispatch, "success", "Created items successfully");

                getProxyServers();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const deleteProxyServer = (proxyServerKey) => {
        const dataItem = {
            proxyServerKey: proxyServerKey
        }

        fetch("/api/administrator/proxyserver",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted item successfully");

                getProxyServers();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const updateProxyServer = (proxyServerKey, isActive) => {
        const dataItem = {
            proxyServerKey: proxyServerKey,
            isActive: isActive,
        }

        fetch("/api/administrator/proxyserver",
            {
                method: "PUT",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Updated item active status");

                getProxyServers();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const validateForm = () => {
        let dataItem = {
            url: url,
            ticketingWebsiteId: undefined,
        };

        if (ticketingWebsite !== undefined
            && ticketingWebsite.ticketingWebsiteId !== "") {
            dataItem.ticketingWebsiteId = parseInt(ticketingWebsite.ticketingWebsiteId);
        }

        if (dataItem.ticketingWebsiteId === undefined
            && dataItem.url === undefined) {
            addNotificationWithTimeout(dispatch, "error", "You must supply a URL or select a ticketing website");
            return null;
        }

        return dataItem;
    }

    const testProxyServer = (testCriteria, proxyServerKey) => {
        const proxyServer = proxyServers.find(element => element.proxyServerKey === proxyServerKey);

        if (!proxyServer) {
            return;
        }

        if (!testCriteria) {
            testCriteria = validateForm();

            if (!testCriteria) {
                return;
            }

            testCriteria.host = proxyServer.host;
            testCriteria.port = proxyServer.port;
            testCriteria.username = proxyServer.username;
            testCriteria.password = proxyServer.password;
        }

        setIsLoading(true);

        fetch(`/api/administrator/testproxyserver`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(testCriteria),
            })
            .then(handleResponse)
            .then(result => {
                setIsLoading(false);

                let proxyServersCopy = proxyServers;

                proxyServersCopy.forEach(dataItem => {
                    if (dataItem.proxyServerKey === proxyServerKey) {
                        dataItem.status = result.isSuccessful;
                    }
                });

                setProxyServers(proxyServersCopy);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    }

    const testProxyServers = () => {
        let dataItem = validateForm();

        if (!dataItem) {
            return;
        }

        //Clear the old proxy server statuses
        let proxyServersCopy = proxyServers;
        proxyServersCopy.forEach(proxyServer => {
            proxyServer.status = undefined;
        });
        setProxyServers(proxyServersCopy);

        setIsLoading(true);

        dataItem.proxies = [];

        proxyServers.forEach(proxyServer => {
            if (proxyServer.selected === true) {
                const proxy = {
                    proxyServerKey: proxyServer.proxyServerKey,
                    host: proxyServer.host,
                    port: proxyServer.port,
                    username: proxyServer.username,
                    password: proxyServer.password,
                }

                dataItem.proxies.push(proxy);
            }
        });

        if (dataItem.proxies.length === 0) {
            setIsLoading(false);
            addNotificationWithTimeout(dispatch, "error", "No proxies selected");
            return;
        }

        fetch(`/api/administrator/testproxyservers`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                let proxyServersCopy = proxyServers;

                proxyServersCopy.forEach(dataItem => {
                    const proxy = result.proxies.find(element => element.proxyServerKey === dataItem.proxyServerKey);

                    if (proxy) {
                        dataItem.status = proxy.isSuccessful;
                    }
                });

                setProxyServers(proxyServersCopy);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);                
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const MyCommandCell = (props) => (
        <CommandCell
            {...props}
            canEdit={false}
            remove={remove}
            keyField="proxyServerKey"
        />
    );

    const UpdateStatusCell = (props) => {
        const proxyServerKey = props.dataItem.proxyServerKey;
        const isActive = props.dataItem.isActive;

        return (
            <td className="k-command-cell">
                <Button
                    type={"button"}
                    onClick={() => updateProxyServer(proxyServerKey, !isActive)}
                    themeColor={isActive ? undefined : "primary"}
                    icon={isActive ? "close" : "plus"}>
                    {isActive ? "Deactivate" : "Activate"}
                </Button>
            </td>
        );
    };

    const StatusCell = (props) => {
        const proxyServerKey = props.dataItem.proxyServerKey;
        const status = props.dataItem.status;

        return (
            <td className="k-table-td">
                <Button
                    type={"button"}
                    onClick={() => testProxyServer(null, proxyServerKey)}
                    fillMode="link"
                    icon={"play"}>
                    Test
                </Button>

                {status !== undefined
                    ? <span className={status ? "text-success" : "text-danger"}>{status ? "Success" : "Failure"}</span>
                    : ""}
            </td>
        );
    };

    const remove = (dataItem) => {
        deleteProxyServer(dataItem.proxyServerKey);
    };

    const addNew = () => {
        //Change the new proxy servers text to an array
        const linesOfText = newProxyServersText.split("\n");

        let newProxyServers = [];
        linesOfText.forEach(lineOfText => {
            const lineOfTextTrimmed = lineOfText.trim();

            if (lineOfTextTrimmed === "") {
                return;
            }

            let lineOfTextSplit = [];

            if (lineOfTextTrimmed.includes(":")) {
                lineOfTextSplit = lineOfTextTrimmed.split(":");
            }
            else if (lineOfTextTrimmed.includes(",")) {
                lineOfTextSplit = lineOfTextTrimmed.split(",");
            }
            else {
                return;
            }

            if (lineOfTextSplit.length !== 4) {
                return;
            }

            const port = parseInt(lineOfTextSplit[1]);
            if (!port) {
                return;
            }

            newProxyServers.push({
                host: lineOfTextSplit[0],
                port: port,
                username: lineOfTextSplit[2],
                password: lineOfTextSplit[3],
            });
        });

        if (newProxyServers.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No proxy servers entered to save");
            return;
        }

        addProxyServers(newProxyServers);
    };

    const deleteAll = () => {
        if (window.confirm("Are you sure you want to delete all of the proxy servers?") === false) {
            return;
        }

        fetch("/api/administrator/proxyservers",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", `Deleted ${result.itemsDeletedCount} proxy servers successfully`);

                getProxyServers();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    };

    const deleteSelected = () => {
        let proxyServerKeys = [];

        proxyServers.forEach(proxyServer => {
            if (proxyServer.selected === true) {
                proxyServerKeys.push(proxyServer.proxyServerKey);
            }
        });

        if (proxyServerKeys.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No proxies selected");
            return;
        }

        if (window.confirm("Are you sure you want to delete the selected proxy servers?") === false) {
            return;
        }

        const dataItem = {
            proxyServerKeys: proxyServerKeys
        }

        fetch("/api/administrator/proxyserver",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", `Deleted ${result.itemsDeletedCount} proxy servers successfully`);

                getProxyServers();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    };

    const ticketingWebsiteChanged = (event) => {
        const value = event.value;
        setTicketingWebsite(value);
    }

    const urlChanged = (event) => {
        const value = event.value;
        setUrl(value);
    }

    useEffect(() => {
        document.title = "Proxy Servers";

        getProxyServers();
        getTicketingWebsites();
    }, []);

    return (
        <div className="administratorReport">
            {isLoading ? <LoadingPanel /> : <></>}

            <div className="k-form k-form-md">
                <div className="k-form-fieldset container">
                    <div className="row">
                        <div className="col-md-5">
                            <div className="k-form-field">
                                <label className="k-label">Url</label>

                                <Input
                                    onChange={urlChanged}
                                    value={url}
                                />
                            </div>                            
                        </div>
                        <div className="col-md-1 text-center">
                            <div className="k-form-field">
                                <label className="k-label">&nbsp;</label>
                                OR
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="k-form-field">
                                <label className="k-label">
                                    Ticketing website
                                </label>

                                <DropDownList
                                    data={ticketingWebsites}
                                    value={ticketingWebsite}
                                    textField="name"
                                    dataItemKey="ticketingWebsiteId"
                                    onChange={ticketingWebsiteChanged}
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="k-form-field">
                                <label className="k-label">&nbsp;</label>

                                <Button
                                    type={"submit"}
                                    themeColor={"primary"}
                                    icon="play"
                                    onClick={() => testProxyServers()}
                                >
                                    Test Selected Proxies
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Grid
                data={proxyServers}
                dataItemKey={"proxyServerKey"}
                resizable={true}
                scrollable="none"
            >
                <GridToolbar>
                    <Button
                        title={"Add new proxy servers"}
                        themeColor={"primary"}
                        icon="plus"
                        onClick={() => setShowDialog(true)}
                    >
                        Add new proxy servers
                    </Button>

                    <Button
                        title={"Delete all proxy servers"}                               
                        icon="delete"
                        onClick={deleteAll}
                    >
                        Delete all proxy servers
                    </Button>

                    <Button
                        title={"Delete selected proxy servers"}
                        icon="delete"
                        onClick={deleteSelected}
                    >
                        Delete selected proxy servers
                    </Button>
                </GridToolbar>
                <Column cell={MyCommandCell} />
                <Column cell={UpdateStatusCell} />
                <Column cell={SelectedCell} headerCell={SelectedHeaderCell} />
                <Column field="host" title={"Host"} />
                <Column field="port" title={"Port"} />
                <Column field="username" title={"Username"} />
                <Column field="password" title={"Password"} />   
                <Column cell={StatusCell} title={"Status"} /> 
                <Column field="dateCreated" title={"Created"} />                        
            </Grid>
            
            {showDialog === false ? <></> :
                <Dialog
                    title={"Proxy Servers (1 proxy per line, use colon or comma to separate parts)"}
                    onClose={() => setShowDialog(false)}
                    width={"90vw"}
                >
                    <div className="mb-3">
                        <TextArea
                            rows={5}
                            value={newProxyServersText} 
                            onChange={(e) => setNewProxyServersText(e.value)}
                        />
                    </div>
                    <div>
                        <Button
                            title={"Save"}
                            themeColor={"primary"}
                            icon="save"
                            onClick={addNew}
                        >
                            Save
                        </Button>
                    </div> 
                </Dialog>
            }
        </div>
    );
}